import React from 'react';
import { Clock, FileCheck2, Trophy, Users } from 'lucide-react';
import { formatLakh } from './HeroCommercialBand';

const Panel = ({ title, icon: Icon, iconClass, action, children, testId }) => (
  <div
    className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-18px_rgba(16,24,40,0.16)]"
    data-testid={testId}
  >
    <div className="flex items-center justify-between gap-3">
      <h2 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
        {Icon && <Icon className={`h-4 w-4 ${iconClass}`} strokeWidth={1.75} />} {title}
      </h2>
      {action}
    </div>
    <div className="mt-4">{children}</div>
  </div>
);

const Ring = ({ percent }) => {
  const radius = 34;
  const circumference = 2 * Math.PI * radius;
  const filled = Math.max(0, Math.min(100, Number(percent) || 0));
  return (
    <svg viewBox="0 0 80 80" className="h-20 w-20 shrink-0">
      <circle cx="40" cy="40" r={radius} fill="none" stroke="#EEF2F7" strokeWidth="8" />
      {filled > 0 && (
        <circle
          cx="40" cy="40" r={radius} fill="none" stroke="#10B981" strokeWidth="8" strokeLinecap="round"
          strokeDasharray={`${(filled / 100) * circumference} ${circumference}`}
          transform="rotate(-90 40 40)"
        />
      )}
      <text x="40" y="44" textAnchor="middle" className="fill-slate-900 font-mono text-[15px] font-bold">
        {filled}%
      </text>
    </svg>
  );
};

const TurnaroundCard = ({ icon: Icon, iconClass, chipClass, value, unit, label, testId }) => (
  <div
    className="flex items-center gap-3 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-18px_rgba(16,24,40,0.16)]"
    data-testid={testId}
  >
    <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${chipClass}`}>
      <Icon className={`h-5 w-5 ${iconClass}`} strokeWidth={1.75} />
    </span>
    <div className="min-w-0">
      <p className="font-mono text-2xl font-bold leading-none tabular-nums text-slate-900">
        {value == null ? '—' : value}
        {value != null && <span className="ml-1 text-sm font-normal text-slate-400">{unit}</span>}
      </p>
      <p className="mt-1 truncate text-xs text-slate-500">{label}</p>
    </div>
  </div>
);

/** Win rate, turnaround and the customers behind the quoted value. */
export const PerformanceTiles = ({ performance }) => {
  const won = Number(performance?.won_value_inr || 0);
  const quoted = Number(performance?.quoted_value_inr || 0);
  const topValue = Math.max(1, ...(performance?.top_customers || []).map((item) => item.value_inr));

  return (
    <div className="space-y-4">
      <Panel
        title="Win rate" icon={Trophy} iconClass="text-emerald-500" testId="performance-win-rate"
        action={(
          <p className="flex items-center gap-2 text-xs text-slate-400">
            <span className="font-mono font-semibold tabular-nums text-emerald-600">{performance?.won_count || 0}</span> won
            <span className="text-slate-200">|</span>
            <span className="font-mono font-semibold tabular-nums text-slate-700">{performance?.lost_count || 0}</span> lost
            <span className="text-slate-200">|</span>
            <span className="font-mono font-semibold tabular-nums text-slate-700">{performance?.pending_count || 0}</span> open
            <span className="text-slate-200">|</span>
            <span className="font-mono font-semibold tabular-nums text-slate-700">{performance?.quoted_count || 0}</span> quoted
          </p>
        )}
      >
        <div className="flex items-center gap-5">
          <Ring percent={performance?.win_rate_count || 0} />
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">Won of quoted</p>
            <p className="mt-1 font-mono text-sm tabular-nums text-slate-700">
              ₹{formatLakh(won)} <span className="text-slate-400">of</span> ₹{formatLakh(quoted)}
            </p>
            <p className="mt-1 text-xs text-slate-400" data-testid="performance-win-rate-basis">
              {performance?.won_count || 0} of {performance?.quoted_count || 0} quotations · {performance?.win_rate_value || 0}% by value
            </p>
            {(performance?.pending_count || 0) > 0 && (
              <p className="mt-0.5 text-xs text-slate-400" data-testid="performance-win-rate-decided">
                {performance?.win_rate_decided || 0}% of the {(performance?.won_count || 0) + (performance?.lost_count || 0)} decided · {performance?.pending_count} still open
              </p>
            )}
          </div>
        </div>
      </Panel>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <TurnaroundCard
          icon={Clock} iconClass="text-blue-600" chipClass="bg-blue-50"
          value={performance?.avg_days_inquiry_to_quote} unit="days" label="Inquiry → quote"
          testId="performance-turnaround"
        />
        <TurnaroundCard
          icon={FileCheck2} iconClass="text-violet-600" chipClass="bg-violet-50"
          value={performance?.avg_days_quote_to_order} unit="days" label="Quote → order"
          testId="performance-turnaround-order"
        />
      </div>

      <Panel title="Top customers by quoted value" icon={Users} iconClass="text-blue-600" testId="performance-top-customers">
        {!(performance?.top_customers || []).length ? (
          <p className="text-sm text-slate-500">No quotations in this period.</p>
        ) : (
          <ul className="space-y-3.5">
            {performance.top_customers.map((entry) => (
              <li key={entry.customer_name} data-testid={`top-customer-${entry.customer_name}`}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-sm text-slate-700">{entry.customer_name}</span>
                  <span className="whitespace-nowrap font-mono text-sm font-semibold tabular-nums text-slate-900">
                    ₹{formatLakh(entry.value_inr)}
                    <span className="ml-2 text-xs font-normal text-slate-400">{entry.count}q</span>
                  </span>
                </div>
                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-blue-500" style={{ width: `${(entry.value_inr / topValue) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    </div>
  );
};
